import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  workOrdersApi,
  type ListWorkOrdersFilters,
} from './service';
import type {
  WorkOrderCreateValues,
  WorkOrderUpdateValues,
} from './schema';
import type { WorkOrder, WorkOrderStatus } from './types';

/**
 * Query key factory for work orders.
 *
 *   workOrderKeys.all                    every cached WO query
 *   workOrderKeys.lists()                all list queries
 *   workOrderKeys.list(filters)          list filtered by vessel/status/type
 *   workOrderKeys.detail(id)             single WO record
 *
 * Filters are part of the key so the same screen can switch filter
 * combinations without invalidating each other; mutations invalidate
 * the entire `lists()` umbrella to be safe.
 */
export const workOrderKeys = {
  all: ['workOrders'] as const,
  lists: () => [...workOrderKeys.all, 'list'] as const,
  list: (filters: ListWorkOrdersFilters) =>
    [...workOrderKeys.lists(), filters] as const,
  detail: (id: string) => [...workOrderKeys.all, 'detail', id] as const,
  events: (id: string) => [...workOrderKeys.all, 'events', id] as const,
  assignments: (id: string) =>
    [...workOrderKeys.all, 'assignments', id] as const,
};

export function useWorkOrders(filters: ListWorkOrdersFilters = {}) {
  return useQuery({
    queryKey: workOrderKeys.list(filters),
    queryFn: () => workOrdersApi.list(filters),
  });
}

export function useWorkOrder(id: string | undefined) {
  return useQuery({
    queryKey: id ? workOrderKeys.detail(id) : workOrderKeys.all,
    queryFn: () => workOrdersApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkOrderCreateValues) => workOrdersApi.create(input),
    onSuccess: (created: WorkOrder) => {
      qc.invalidateQueries({ queryKey: workOrderKeys.lists() });
      qc.setQueryData(workOrderKeys.detail(created.id), created);
    },
  });
}

export function useUpdateWorkOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: WorkOrderUpdateValues) =>
      workOrdersApi.update(id, patch),
    onSuccess: (updated: WorkOrder) => {
      qc.invalidateQueries({ queryKey: workOrderKeys.lists() });
      qc.setQueryData(workOrderKeys.detail(id), updated);
    },
  });
}

export function useDeleteWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workOrdersApi.remove(id),
    onSuccess: (_void, id) => {
      qc.invalidateQueries({ queryKey: workOrderKeys.all });
      qc.removeQueries({ queryKey: workOrderKeys.detail(id) });
    },
  });
}

export function useWorkOrderEvents(id: string | undefined) {
  return useQuery({
    queryKey: id ? workOrderKeys.events(id) : workOrderKeys.all,
    queryFn: () => workOrdersApi.getEvents(id as string),
    enabled: Boolean(id),
  });
}

export function useWorkOrderAssignments(id: string | undefined) {
  return useQuery({
    queryKey: id ? workOrderKeys.assignments(id) : workOrderKeys.all,
    queryFn: () => workOrdersApi.getAssignments(id as string),
    enabled: Boolean(id),
  });
}

/**
 * useTransitionWorkOrder — mutation hook for state machine
 * transitions. Invalidates the entire WO sub-tree because a
 * transition can affect lists (status changed), the detail (timestamps
 * + status), the timeline (status_log event was appended), and
 * potentially the assignments (cancel-back-to-draft removes them).
 */
/**
 * L16 — bulk-update WO priority. Invalidates the entire WO list cache
 * because the table renders the priority badge inline. Detail pages
 * for affected ids are also invalidated so a freshly-bulk-updated WO
 * shows the new priority on a refresh.
 */
export function useBulkUpdateWorkOrderPriority() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      ids: string[];
      priority: 'low' | 'normal' | 'high' | 'critical';
    }) => workOrdersApi.bulkUpdatePriority(input),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: workOrderKeys.lists() });
      for (const id of result.updatedIds) {
        qc.invalidateQueries({ queryKey: workOrderKeys.detail(id) });
      }
    },
  });
}

export function useBulkDeleteWorkOrders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[] }) =>
      workOrdersApi.bulkDelete(input),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: workOrderKeys.lists() });
      for (const id of result.deletedIds) {
        qc.removeQueries({ queryKey: workOrderKeys.detail(id) });
      }
    },
  });
}

export function useTransitionWorkOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      newStatus: WorkOrderStatus;
      cancelReason?: string;
      actualHours?: number;
    }) => workOrdersApi.transition(id, body),
    onSuccess: (updated: WorkOrder) => {
      qc.invalidateQueries({ queryKey: workOrderKeys.lists() });
      qc.invalidateQueries({ queryKey: workOrderKeys.events(id) });
      qc.invalidateQueries({ queryKey: workOrderKeys.assignments(id) });
      qc.setQueryData(workOrderKeys.detail(id), updated);
    },
  });
}
